import React from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator, View, Image, Text, Heading, Button, useTheme, useAuthenticator } from '@aws-amplify/ui-react';
import { signUp, SignUpInput } from '@aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Link from '@mui/material/Link';
import LandingPage from './landing-page/LandingPage';
import LifeSciencesPage from './landing-page/LifeSciencesPage';
import Dashboard from './dashboard/Dashboard';
import SignUpPage from './pages/SignUpPage';
import * as amplitude from '@amplitude/analytics-browser';
import config from './utils/Constants';

Amplify.configure(config.awsExports);
amplitude.init('af66f6e5f9b7876eef3a2cbef476f70d');


const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Image
          alt="Cynthia Data Logo"
          src={process.env.PUBLIC_URL + '/logo-blue.png'}
        />
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text color={tokens.colors.neutral[80]}>
          &copy; All Rights Reserved
        </Text>
      </View>
    );
  },

  SignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Sign in to your account
        </Heading>
      );
    },
    Footer() {
      const { toForgotPassword } = useAuthenticator();

      return (
        <View textAlign="center">
          <Button
            fontWeight="normal"
            onClick={toForgotPassword}
            size="small"
            variation="link"
          >
            Reset Password
          </Button>
        </View>
      );
    },
  },

  SignUp: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Create a new account
        </Heading>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();

      return (
        <View textAlign="center">
          <Button
            fontWeight="normal"
            onClick={toSignIn}
            size="small"
            variation="link"
          >
            Back to Sign In
          </Button>
        </View>
      );
    },
  },
  ConfirmSignUp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>{'Copyright © '}
      <Link href="#">Cynthia&nbsp;</Link>
      {new Date().getFullYear()}</Text>;
    },
  },
  SetupTotp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>{'Copyright © '}
      <Link href="#">Cynthia&nbsp;</Link>
      {new Date().getFullYear()}</Text>;
    },
  },
  ConfirmSignIn: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>{'Copyright © '}
      <Link href="#">Cynthia&nbsp;</Link>
      {new Date().getFullYear()}</Text>;
    },
  },
  ForgotPassword: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>{'Copyright © '}
      <Link href="#">Cynthia&nbsp;</Link>
      {new Date().getFullYear()}</Text>;
    },
  },
  ConfirmResetPassword: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>{'Copyright © '}
      <Link href="#">Cynthia&nbsp;</Link>
      {new Date().getFullYear()}</Text>;
    },
  },
};

const formFields = {
  signIn: {
    username: {
      placeholder: 'Enter your email',
    },
  },
  signUp: {
    username: {
      label: 'Email:',
      placeholder: 'Enter your email:',
      isRequired: true,
      order: 1,
    },
    password: {
      label: 'Password:',
      placeholder: 'Enter your Password:',
      isRequired: false,
      order: 2,
    },
    confirm_password: {
      label: 'Confirm Password:',
      order: 3,
    },
  },
  forceNewPassword: {
    password: {
      placeholder: 'Enter your Password:',
    },
  },
  forgotPassword: {
    username: {
      placeholder: 'Enter your email:',
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      placeholder: 'Enter your Confirmation Code:',
      label: 'Confirmation Code:',
      isRequired: false,
    },
    confirm_password: {
      placeholder: 'Confirm your Password Please:',
    },
  },
  setupTotp: {
    QR: {
      totpIssuer: 'test issuer',
      totpUsername: 'amplify_qr_test_user',
    },
    confirmation_code: {
      label: 'Enter your Confirmation Code:',
      placeholder: 'Enter your Confirmation Code:',
      isRequired: false,
    },
  },
  confirmSignIn: {
    confirmation_code: {
      label: 'Enter your Confirmation Code:',
      placeholder: 'Enter your Confirmation Code:',
      isRequired: false,
    },
  },
};

// Redirects authenticated users away from the landing page to the dashboard.
// Shows nothing while Amplify resolves session state to prevent a flash of the landing page.
function RootRoute() {
  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);
  if (authStatus === 'configuring') return null;
  if (authStatus === 'authenticated') return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

// Inner component rendered inside Authenticator for the /dashboard (no tenantId) route.
// Redirects to /dashboard/:tenantId if a tenant is already selected in Redux;
// otherwise renders Dashboard so the tenant picker is shown.
function DashboardIndexInner({ signOut, user }) {
  const selectedTenant = useSelector((state) => state.user.selectedTenant);
  if (selectedTenant?.id != null) {
    return <Navigate to={`/dashboard/${selectedTenant.id}`} replace />;
  }
  return <Dashboard signOut={signOut} user={user} />;
}

export default function App() {
  return (
    <Authenticator.Provider>
      <Router>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route
            path="/dashboard"
            element={
              <Authenticator formFields={formFields} components={components} socialProviders={['google']}>
                {({ signOut, user }) => (
                  <DashboardIndexInner signOut={signOut} user={user} />
                )}
              </Authenticator>
            }
          />
          <Route
            path="/dashboard/:tenantId/*"
            element={
              <Authenticator formFields={formFields} components={components} socialProviders={['google']}>
                {({ signOut, user }) => (
                  <Dashboard signOut={signOut} user={user} />
                )}
              </Authenticator>
            }
          />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/life-sciences" element={<LifeSciencesPage />} />
        </Routes>
      </Router>
    </Authenticator.Provider>
  );
}